import { Link } from "react-router-dom";
import { CheckCircle } from "lucide-react";

import {
  BABY_DISCHARGE_LABEL,
  isBabyDischargeSummary,
} from "features/results/utils/neonatalDischarge";
import { pathGenerators } from "utils/routes";

import { ResultCorrectionBadge } from "../../components/ResultCorrectionBadge/ResultCorrectionBadge";
import { formatCommonDate } from "../../utils/formatters";
import styles from "./columns.module.css";
import { TruncatedTestNames } from "./TruncatedTestNames";

const TestNames = ({ result: row }) => {
  const hasTestNames = row.results?.some((r) => r.testName);

  if (hasTestNames) {
    return (
      <span>
        {row.results.map((r, i) => (
          <span key={i} className={r.warning ? styles.warningText : undefined}>
            {r.testName}
            {i !== row.results.length - 1 && ", "}
          </span>
        ))}
      </span>
    );
  }

  // Other types (referrals, discharges, appointments) carry no test names —
  // show the test names list, then fall back to the message type.
  return (
    <span>
      {row.testNamesList?.filter(Boolean).join(", ") || row.resultType || "-"}
    </span>
  );
};

export const getCommonStaffColumns = ({ navigateToPatient }) => [
  {
    id: "date",
    header: "Date",
    width: "100px",
    render: (row) => formatCommonDate(row.orderDate),
  },
  {
    id: "patient",
    header: "Patient",
    width: "130px",
    flexGrow: 1,
    render: (row) => {
      const name = row.patient
        ? `${row.patient.firstName} ${row.patient.surname}`
        : row.patientInformationExternal?.fullName || "Unknown Patient";

      /* The row is on her file; the document is about her baby (KIN-1949). Said under the name
         rather than in the Type column, because what it changes is *whose* record this is about —
         a clinician scanning her documents would otherwise read it as one of her own. */
      const babyLabel = isBabyDischargeSummary(row) ? (
        <span className={styles.babyDischarge}>{BABY_DISCHARGE_LABEL}</span>
      ) : null;

      return row.patientId ? (
        <span className={styles.patientCell}>
          <Link
            to={pathGenerators.patient.results(row.patientId)}
            className={styles.patientName}
            onClick={(e) => navigateToPatient(e, row.patientId)}
          >
            {name}
          </Link>
          {babyLabel}
        </span>
      ) : (
        <span className={styles.patientCell}>
          <span>{name}</span>
          {babyLabel}
        </span>
      );
    },
  },
  {
    id: "test",
    header: "Test",
    width: "216px",
    render: (row) => (
      <span className={styles.testCell}>
        <TruncatedTestNames>
          <TestNames result={row} />
        </TruncatedTestNames>
        <ResultCorrectionBadge result={row} />
      </span>
    ),
  },
  {
    id: "status",
    header: "",
    width: "20px",
    render: (row) =>
      row.orderStatus === "CONFIRMED" ? (
        <CheckCircle size={16} className={styles.confirmedIcon} />
      ) : null,
  },
];
